<style lang="scss">
    .scatterplot {
        position: relative;
    }

    svg {
        width: 100%;
        height: auto;
        display: block;
    }
    .label {
        fill: #FFF;
    }

    .tick text, .tick line {
        fill: #FFF !important;
    }

    .axis path,
    .axis line {
        fill: none;
        stroke: #FFF;
        shape-rendering: crispEdges;
    }

    .dimmed {
        opacity: .1 !important;
    }
</style>
<template>
    <div class="scatterplot">
        <tooltip v-bind="tooltip" />
        <div class="scatterplot__visualization"></div>
    </div>
</template>
<script>
import {
    select,
    nest,
    hierarchy,
    scaleLinear,
    min,
    max,
    axisBottom,
    axisLeft,
    selectAll,
    event,
} from 'd3'
const Tooltip = () => import(/* webpackChunkName: 'Tooltip' */ '@/components/Tooltip')

export default {
    name: 'Scatterplot',
    props: {
        hierarchy: {
            type: Object,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        top: {
            type: Number,
            default: 20,
        },
        right: {
            type: Number,
            default: 20,
        },
        bottom: {
            type: Number,
            default: 40,
        },
        left: {
            type: Number,
            default: 40,
        },
        width: {
            type: Number,
            default: 600,
        },
        height: {
            type: Number,
            default: 600,
        },
        selectedType: {
            type: String,
        },
        xLabel: {
            type: String,
            default: 'Pseudotime X',
        },
        yLabel: {
            type: String,
            default: 'Pseudotime Y',
        },
    },
    data: () => ({
        tooltip: {
            active: false,
            text: '',
            top: 0,
            left: 0,
        },
    }),
    computed: {
        scatterplot() {
            return select(this.$el.querySelector('.scatterplot__visualization'))
                .append('svg')
                .attr('viewBox', `0 0 ${this.dimensions.width + this.dimensions.left + this.dimensions.right} ${this.dimensions.height + this.dimensions.top + this.dimensions.bottom}`)
                .append('g')
                .attr('transform', `translate(${this.dimensions.left}, ${this.dimensions.top})`)
        },
        selectedData() {
            let dataKeys = Object.keys(this.data)
            if (this.selectedType) {
                dataKeys = dataKeys.filter(type => type === this.selectedType)
            }
            return dataKeys.reduce((sum, type) => [...sum, ...this.data[type]], [])
        },
        selectedHirearchy() {
            if (!this.selectedType) {
                return this.hierarchy
            }
            return {
                ...this.hierarchy,
                children: this.hierarchy.children.filter(child => child.key === this.selectedType),
            }
        },
        groupedData() {
            return nest()
                .key(d => `${d.subset}-${d.name}`)
                .map(this.selectedData)
        },
        root() {
            return hierarchy(this.selectedHirearchy)
                .sum(d => d.size)
                .each(d => {
                    if (d.parent && this.groupedData.has(`${d.parent.data.name}-${d.data.name}`)) {
                        d.data.dataValues = this.groupedData.get(`${d.parent.data.name}-${d.data.name}`)
                    }
                    d.data.selected = false
                })
        },
        scatterX() {
            return scaleLinear()
                .domain([min(this.selectedData, d => Number(d.x)), max(this.selectedData, d => Number(d.x))])
                .range([0, this.dimensions.width])
        },
        scatterY() {
            return scaleLinear()
                .domain([min(this.selectedData, d => Number(d.y)), max(this.selectedData, d => Number(d.y))])
                .range([this.dimensions.height, 0])
        },
        xAxis() {
            return axisBottom(this.scatterX)
        },
        yAxis() {
            return axisLeft(this.scatterY)
        },
        dimensions() {
            let width = this.width - this.left - this.right
            let height = this.height - this.top - this.bottom
            return {
                top: this.top,
                right: this.right,
                bottom: this.bottom,
                left: this.left,
                width,
                height,
                radius: Math.min(width, height) / 2,
            }
        },
    },
    methods: {
        click(d) {
            this.$emit('click', d.subset.toLowerCase())
        },
        highlightRelatedNodes(node) {
            selectAll('.letiant').classed('dimmed', true)
            node.each(d => selectAll(`.${d.parent ? `${d.parent.data.key}-` : ''}${d.data.key}`).classed('dimmed', false))
        },
    },
    mounted() {
        // X Axis
        this.scatterplot.append('g')
            .attr('class', 'x axis')
            .attr('transform', `translate(-25, ${this.dimensions.height + 20})`)
            // .call(this.xAxis)
            .append('text') // X-axis Label
            .attr('class', 'label')
            .attr('y', -10)
            .attr('x', this.dimensions.width)
            .attr('dy', '.71em')
            .style('text-anchor', 'end')
            .text(this.xLabel)

        // Y Axis
        this.scatterplot.append('g')
            .attr('class', 'y axis')
            .attr('transform', 'translate(-25, 20)')
            // .call(this.yAxis)
            .append('text') // y-axis Label
            .attr('class', 'label')
            .attr('transform', 'rotate(-90)')
            .attr('x', 0)
            .attr('y', 5)
            .attr('dy', '.71em')
            .style('text-anchor', 'end')
            .text(this.yLabel)

        // scatterplot
        this.root.each(node => {
            if ('dataValues' in node.data) {
                let cluster = this.scatterplot.append('g')
                    .attr('class', () => `letiant cluster ${node.parent ? `${node.parent.data.key}-` : ''}${node.data.key}`)
                    .style('fill', () => node.data.color)
                    .on('mouseenter', () => {
                        this.highlightRelatedNodes(node)
                        let offset = this.$el.querySelector('svg').getBoundingClientRect()
                        this.tooltip = {
                            active: true,
                            text: node.data.name,
                            top: event.pageY - offset.top - window.pageYOffset,
                            left: event.pageX - offset.left - window.pageXOffset,
                        }
                    })
                    .on('mouseleave', () => {
                        selectAll('.letiant').classed('dimmed', false)
                        this.tooltip.active = false
                    })

                cluster.selectAll('circle')
                    .data(node.data.dataValues)
                    .enter()
                    .append('circle')
                    .on('click', this.click)
                    .attr('r', 3)
                    .attr('cx', d => this.scatterX(d.x))
                    .attr('cy', d => this.scatterY(d.y))
            }
        })
    },
    components: {
        Tooltip,
    },
}
</script>
